/**
 * Reading a finished poker hand off the wire.
 *
 * Kept apart from the component because this is the half that can be wrong
 * quietly. The drawing is either on the screen or it is not; the reading has
 * to decide who won, which hands to show, and whether the packet describes a
 * hand at all - and every one of those has a wrong answer that still renders.
 *
 * See `ui/Showdown.tsx` for why a finished hand needs recording at all: poker
 * settles and re-deals inside one reduction, so the showdown is over before
 * any packet exists.
 */

import type { MatchView } from '../net/match.js';

/**
 * How long the finished hand stays up.
 *
 * Lives here rather than in the component because two things are timed off
 * it: the panel, and the chips that fly to the winner once it clears. A
 * payout that set off while the cards were still being read would be the
 * thing it was meant to explain, happening underneath the explanation.
 */
export const SHOWDOWN_MS = 5000;

/**
 * How long after a hand settles the pot sets off towards the winner.
 *
 * Inside the panel's five seconds, deliberately, and this took two tries to
 * get right. Sending the chips once the panel CLEARED looked correct in
 * isolation and was wrong on a table: the next hand is dealt the instant this
 * one settles and carries on underneath, so five seconds later the pot of a
 * finished hand went sliding across a board that was already on the turn.
 *
 * The panel is the frozen moment. The chips have to move inside it, which is
 * also the order the sequence was asked for - cards, then the hand named, then
 * the money. Long enough to have read who won before anything moves; short
 * enough that the flight lands well before the panel goes.
 */
export const PAYOUT_MS = 1800;

export interface ShowdownHand {
  seat: number;
  /** What they had, as the toolkit describes it: "two pair, kings and fives". */
  hand: string;
  cards: string[];
  /** What this seat was paid. Zero for a hand that lost. */
  won: number;
}

export interface Showdown {
  /** Everybody who showed, in the order the game listed them. Empty when all folded. */
  hands: ShowdownHand[];
  /** The community cards the hands were read against. Empty when nobody showed. */
  board: string[];
  /** Seats paid and what they took, whether or not anybody showed. */
  takers: Array<{ seat: number; amount: number }>;
  /** Rises once per hand, so the same result twice registers as two events. */
  n: number;
}

function numbers(value: unknown): number[] {
  return Array.isArray(value) ? value.filter((item): item is number => typeof item === 'number') : [];
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

/**
 * The finished hand in a view, or null if this packet has none.
 *
 * Returns null rather than an empty result for "no hand yet", because the
 * component's hold is driven by a counter and a zero is the baseline - a
 * shape that is present but empty would hold an empty banner over the table.
 */
export function readShowdown(view: MatchView): Showdown | null {
  const raw = view.view['lastHand'];
  if (typeof raw !== 'object' || raw === null) return null;

  const value = raw as { shown?: unknown; board?: unknown; paid?: unknown; handNumber?: unknown };
  if (typeof value.handNumber !== 'number') return null;

  const paid = numbers(value.paid);
  const takers: Array<{ seat: number; amount: number }> = [];
  for (let seat = 0; seat < paid.length; seat++) {
    const amount = paid[seat] ?? 0;
    if (amount > 0) takers.push({ seat, amount });
  }

  /*
   * A hand everybody folded out of has no `shown` entries, and that is a
   * result worth showing rather than a reason to bail - somebody still won
   * it, and their cards stay secret because nobody paid to see them.
   */
  const hands: ShowdownHand[] = [];
  if (Array.isArray(value.shown)) {
    for (const entry of value.shown) {
      if (typeof entry !== 'object' || entry === null) continue;
      const row = entry as { seat?: unknown; hand?: unknown; cards?: unknown };
      if (typeof row.seat !== 'number' || typeof row.hand !== 'string') continue;
      hands.push({
        seat: row.seat,
        hand: row.hand,
        cards: strings(row.cards),
        // Paid is the whole truth about who won: a split pot pays two seats,
        // and both of them won. Derived here rather than shipped as a second
        // field, so there is one answer to the question.
        won: paid[row.seat] ?? 0,
      });
    }
  }

  // Nobody shown and nobody paid is not a hand, it is a fresh table.
  if (hands.length === 0 && takers.length === 0) return null;

  return { hands, board: strings(value.board), takers, n: value.handNumber };
}

/** "a pair of kings" as a label. The toolkit writes prose; a card wants a title. */
export function titled(hand: string): string {
  return hand.charAt(0).toUpperCase() + hand.slice(1);
}

/**
 * The sentence under the cards.
 *
 * Said in words as well as in colour and position, because a screen reader
 * gets nothing from a gold border - and neither does anybody glancing up from
 * across the room. `name` is passed in rather than looked up here so this
 * stays a pure function of the result.
 */
export function showdownLine(result: Showdown, name: (seat: number) => string): string {
  if (result.hands.length === 0) {
    return result.takers.map((taker) => `${name(taker.seat)} wins ${String(taker.amount)}`).join(' · ');
  }
  return result.hands
    .filter((entry) => entry.won > 0)
    .map((entry) => `${name(entry.seat)} wins ${String(entry.won)} with ${entry.hand}`)
    .join(' · ');
}

/**
 * Who the pot goes to, and how much each of them gets.
 *
 * Works for both kinds of ending: a showdown pays the hands that were shown,
 * and a hand everybody folded out of pays the one player left. `takers`
 * already holds both, which is why it is built whether or not anybody showed.
 */
export function payoutOf(result: Showdown): Array<{ seat: number; amount: number }> {
  return result.takers;
}
